import produce from 'immer';
import {
    fetchCampaignHostBillingHistorySuccess,
    fetchCampaignHostCreationAccessSuccess,
    fetchCampaignHostMineSuccess,
    fetchCampaignHostPackages,
    fetchCampaignHostPackagesFail,
    fetchCampaignHostPackagesSuccess,
    fetchCampaignHostPricing,
    fetchCampaignHostPricingFail,
    fetchCampaignHostPricingSuccess,
} from './actions';
import { extractCampaignHostPackages } from '../../utils/campaignHostSubscriptionApi';
import { types, type ICampaignHostSubscriptionState } from './types';
import { types as AuthTypes } from '../authentication/types';
import { restoreInitialState } from '../../utils/AppUtils';

const initialState: ICampaignHostSubscriptionState = {
    pricing: null,
    catalogPackages: [],
    subscription: null,
    creationAccess: null,
    billingHistory: null,
    fetchingCatalogPackages: false,
    fetchingPricing: false,
    fetchingMine: false,
    fetchingCreationAccess: false,
    fetchingBillingHistory: false,
    checkingOut: false,
    canceling: false,
    updatingPaymentMethod: false,
    tierModalOpen: false,
    checkoutClientSecret: null,
    checkoutPaymentIntentId: null,
    error: null,
    enterprisePackage: null,
    fetchingEnterprisePackage: false,
    grantingEnterprise: false,
    enterpriseGrantResult: null,
};

export function campaignHostSubscriptionReducer(state = initialState, action: any) {
    const { type, payload } = action;

    switch (type) {
        case types.FETCH_PACKAGES:
        case fetchCampaignHostPackages.type:
            return produce(state, draft => {
                draft.fetchingCatalogPackages = true;
            });
        case types.FETCH_PACKAGES_SUCCESS:
        case fetchCampaignHostPackagesSuccess.type:
            return produce(state, draft => {
                draft.fetchingCatalogPackages = false;
                draft.catalogPackages = Array.isArray(payload) ? payload : extractCampaignHostPackages(payload);
            });
        case types.FETCH_PACKAGES_FAIL:
        case fetchCampaignHostPackagesFail.type:
            return produce(state, draft => {
                draft.fetchingCatalogPackages = false;
                draft.error = payload;
            });

        case types.FETCH_PRICING:
        case fetchCampaignHostPricing.type:
            return produce(state, draft => {
                draft.fetchingPricing = true;
            });
        case types.FETCH_PRICING_SUCCESS:
        case fetchCampaignHostPricingSuccess.type:
            return produce(state, draft => {
                draft.fetchingPricing = false;
                draft.pricing = payload;
                if (payload?.creation_access) {
                    draft.creationAccess = payload.creation_access;
                }
            });
        case types.FETCH_PRICING_FAIL:
        case fetchCampaignHostPricingFail.type:
            return produce(state, draft => {
                draft.fetchingPricing = false;
                draft.error = payload;
            });

        case types.FETCH_MINE:
            return produce(state, draft => {
                draft.fetchingMine = true;
            });
        case types.FETCH_MINE_SUCCESS:
        case fetchCampaignHostMineSuccess.type:
            return produce(state, draft => {
                draft.fetchingMine = false;
                draft.subscription = payload ?? null;
            });
        case types.FETCH_MINE_FAIL:
            return produce(state, draft => {
                draft.fetchingMine = false;
                draft.subscription = null;
                draft.error = payload;
            });

        case types.FETCH_CREATION_ACCESS:
            return produce(state, draft => {
                draft.fetchingCreationAccess = true;
            });
        case types.FETCH_CREATION_ACCESS_SUCCESS:
        case fetchCampaignHostCreationAccessSuccess.type:
            return produce(state, draft => {
                draft.fetchingCreationAccess = false;
                draft.creationAccess = payload;
            });
        case types.FETCH_CREATION_ACCESS_FAIL:
            return produce(state, draft => {
                draft.fetchingCreationAccess = false;
                draft.error = payload;
            });

        case types.FETCH_BILLING_HISTORY:
            return produce(state, draft => {
                draft.fetchingBillingHistory = true;
            });
        case types.FETCH_BILLING_HISTORY_SUCCESS:
        case fetchCampaignHostBillingHistorySuccess.type:
            return produce(state, draft => {
                draft.fetchingBillingHistory = false;
                draft.billingHistory = payload;
            });
        case types.FETCH_BILLING_HISTORY_FAIL:
            return produce(state, draft => {
                draft.fetchingBillingHistory = false;
                draft.error = payload;
            });

        case types.SUBSCRIBE_MONTHLY:
        case types.PURCHASE_LTD:
            return produce(state, draft => {
                draft.checkingOut = true;
                draft.checkoutClientSecret = null;
                draft.checkoutPaymentIntentId = null;
            });
        case types.SUBSCRIBE_MONTHLY_SUCCESS:
            return produce(state, draft => {
                draft.checkingOut = false;
                if (payload?.subscription) draft.subscription = payload.subscription;
                draft.checkoutClientSecret = payload?.clientSecret ?? null;
            });
        case types.SUBSCRIBE_MONTHLY_FAIL:
        case types.PURCHASE_LTD_FAIL:
            return produce(state, draft => {
                draft.checkingOut = false;
                draft.error = payload;
            });
        case types.PURCHASE_LTD_SUCCESS:
            return produce(state, draft => {
                draft.checkingOut = false;
                draft.checkoutClientSecret = payload.clientSecret;
                draft.checkoutPaymentIntentId = payload.paymentIntentId;
                if (payload.subscription) draft.subscription = payload.subscription;
            });

        case types.CANCEL_SUBSCRIPTION:
            return produce(state, draft => {
                draft.canceling = true;
            });
        case types.CANCEL_SUBSCRIPTION_SUCCESS:
            return produce(state, draft => {
                draft.canceling = false;
                draft.subscription = payload;
            });
        case types.CANCEL_SUBSCRIPTION_FAIL:
            return produce(state, draft => {
                draft.canceling = false;
                draft.error = payload;
            });

        case types.UPDATE_PAYMENT_METHOD:
            return produce(state, draft => {
                draft.updatingPaymentMethod = true;
            });
        case types.UPDATE_PAYMENT_METHOD_SUCCESS:
            return produce(state, draft => {
                draft.updatingPaymentMethod = false;
                draft.subscription = payload;
            });
        case types.UPDATE_PAYMENT_METHOD_FAIL:
            return produce(state, draft => {
                draft.updatingPaymentMethod = false;
                draft.error = payload;
            });

        case types.SET_TIER_MODAL_OPEN:
            return produce(state, draft => {
                draft.tierModalOpen = payload;
            });
        case types.CLEAR_CHECKOUT:
            return produce(state, draft => {
                draft.checkoutClientSecret = null;
                draft.checkoutPaymentIntentId = null;
            });

        case types.FETCH_ENTERPRISE_PACKAGE:
            return produce(state, draft => {
                draft.fetchingEnterprisePackage = true;
            });
        case types.FETCH_ENTERPRISE_PACKAGE_SUCCESS:
            return produce(state, draft => {
                draft.fetchingEnterprisePackage = false;
                draft.enterprisePackage = payload;
            });
        case types.FETCH_ENTERPRISE_PACKAGE_FAIL:
            return produce(state, draft => {
                draft.fetchingEnterprisePackage = false;
                draft.error = payload;
            });

        case types.GRANT_ENTERPRISE:
            return produce(state, draft => {
                draft.grantingEnterprise = true;
                draft.enterpriseGrantResult = null;
            });
        case types.GRANT_ENTERPRISE_SUCCESS:
            return produce(state, draft => {
                draft.grantingEnterprise = false;
                draft.enterpriseGrantResult = payload;
            });
        case types.GRANT_ENTERPRISE_FAIL:
            return produce(state, draft => {
                draft.grantingEnterprise = false;
                draft.error = payload;
            });
        case types.CLEAR_ENTERPRISE_GRANT:
            return produce(state, draft => {
                draft.enterpriseGrantResult = null;
                draft.grantingEnterprise = false;
            });

        case AuthTypes.LOGOUT:
        case AuthTypes.LOGOUT_SUCCESS:
        case AuthTypes.LOGOUT_FAILED:
            // Host entitlements (incl. instagram_automation) must not leak to the next account.
            return restoreInitialState(state, initialState);

        default:
            return state;
    }
}
